import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../composants/Button/Button';
import Banner from '../../../../composants/Banner/Banner';
import TableauDeBord from '../../../../composants/TableauDeBord/TableauDeBord';
import CompteARebourd from '../../../../composants/CompteARebourd/CompteARebourd';
import SignatureFlow from '../SignatureContrat/SignatureFlow';
import ValidationPopUp from '../../../../composants/ValidationPopUp/ValidationPopUp';
import { numeroCandidature, titreAnnulationCandidature, texteAnnulationCandidature } from '../../annulationCandidatureTexte';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/export.svg';
import './MesCandidaturesTab.css';
import '../../../../composants/DocumentsList/DocumentsList.css';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';
import { getParisLabel } from '../../../../utils/timezone';


// ── Types ─────────────────────────────────────────────────────────────────────

export interface CandidatureResumee {
    id: number;
    identifiant_candidature?: number | null;
    ref_avis: string;
    accord_cadre_nom: string;
    lot_technique_libelle: string;
    statut: 'brouillon' | 'deposee' | 'a_signer' | 'signee' | 'expire' | 'annulee';
    phase?: number;
    created_at: string;
    deposee_at?: string;
    date_limite_candidature?: string;
    /** "ac27" — prévu pour d'autres modules futurs */
    source_module: string;
    /** Faux si l'utilisateur est cotraitant d'un groupement (non-mandataire) */
    est_mandataire?: boolean;
    /** Verdict d'annulation calculé par le back. Le front l'affiche, il ne le rejoue pas. */
    peut_annuler?: boolean;
    /** Horodatage de l'annulation, affiché sous le badge. Null hors annulation. */
    annulee_at?: string | null;
}

export interface InvitationGroupementResumee {
    id: number;
    status: 'pending' | 'accepted' | 'refused';
    invited_at?: string;
    entrepriseMandataire?: { id: number; nom: string; siret: string };
    candidature: {
        id: number;
        lot_technique?: { code: string; nom: string };
        entite?: { nom: string };
    };
}

interface MesCandidaturesTabProps {
    candidatures: CandidatureResumee[];
    invitations: InvitationGroupementResumee[];
    loading: boolean;
    onRefresh: () => void;
    onDeleteCandidature?: (id: number) => void;
    accordCadreName?: string;
    /** Déclenche le bridge-token vers le site démo */
    onDemoClick?: () => void;
    demoLoading?: boolean;
}

// ── StatutBadge ───────────────────────────────────────────────────────────────

function StatutBadge({ statut, isDemo, deposee_at, annulee_at }: { statut: CandidatureResumee['statut']; isDemo?: boolean; deposee_at?: string; annulee_at?: string | null }) {
    if (isDemo) {
        return (
            <span
                className="status-badge"
                style={{ color: '#7c3aed', fontWeight: 700, textAlign: 'left', display: 'inline-block' }}
            >
                Simulation
            </span>
        );
    }
    const config: Record<string, { label: string; color: string }> = {
        brouillon: { label: 'Brouillon', color: '#6b7280' },
        deposee: { label: 'Déposée', color: '#00a708' },
        a_signer: { label: 'À signer', color: '#f97316' },
        signee: { label: 'Signé', color: '#00a708' },
        expire: { label: 'Expiré', color: '#d90e0e' },
        annulee: { label: 'Annulée', color: '#d90e0e' },
    };
    const { label, color } = config[statut] ?? config.brouillon;

    // Deux statuts portent un horodatage a afficher sous le libelle : le depot
    // et l'annulation. Meme rendu pour les deux, le libelle vient de config.
    const horodatage = statut === 'deposee' ? deposee_at
        : statut === 'annulee' ? annulee_at
            : null;

    if (horodatage) {
        const d = new Date(horodatage);
        if (!isNaN(d.getTime())) {
            const date = d.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
            const time = d.toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', second: '2-digit' });
            return (
                <span className="status-badge" style={{ textAlign: 'left', display: 'inline-block' }}>
                    <span style={{ color, fontWeight: 700 }}>{label}</span>
                    <br />
                    <span style={{ color: '#111827', fontWeight: 400, fontSize: '0.8em' }}>le {date} {time}</span>
                    <span style={{ display: 'block', fontSize: '0.75em', color: '#6b7280' }}>{getParisLabel()}</span>
                </span>
            );
        }
    }

    return (
        <span
            className="status-badge"
            style={{ color, fontWeight: 700, textAlign: 'left', display: 'inline-block' }}
        >
            {label}
        </span>
    );
}

// ── ModalInvitationsGroupement ────────────────────────────────────────────────

export function ModalInvitationsGroupement({
    invitations,
    accordCadreName,
    onClose,
    onHandled,
}: {
    invitations: InvitationGroupementResumee[];
    accordCadreName?: string;
    onClose: () => void;
    onHandled: () => void;
}) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    const handleAccept = (invId: number) => {
        fetch(`${apiUrl}/publications/ac27/groupement-invitations/${invId}/accept`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
        }).then(() => onHandled());
    };

    const handleRefuse = (invId: number) => {
        fetch(`${apiUrl}/publications/ac27/groupement-invitations/${invId}/refuse`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ raison: '' }),
        }).then(() => onHandled());
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Invitations groupement en attente ({invitations.length})</h2>
                    <button className="modal-close-btn" onClick={onClose} aria-label="Fermer">
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <Banner
                        variant="info"
                        message="En acceptant le groupement, vous autorisez le chef de groupement à accéder aux éléments de votre profil."
                    />
                    {invitations.map((inv) => (
                        <div key={inv.id} className="invitation-card">
                            <div className="invitation-info">
                                <p className="invitation-title">
                                    Invitation groupement —{' '}
                                    {inv.candidature.lot_technique
                                        ? `Lot ${inv.candidature.lot_technique.code} — ${inv.candidature.lot_technique.nom}`
                                        : `Candidature #${inv.candidature.id}`}
                                </p>
                                {accordCadreName && (
                                    <p className="invitation-ac">
                                        Accord-Cadre : <strong>{accordCadreName}</strong>
                                    </p>
                                )}
                                {(inv.entrepriseMandataire ?? inv.candidature.entite) && (
                                    <p className="invitation-mandataire">
                                        Mandataire :{' '}
                                        <strong>
                                            {inv.entrepriseMandataire?.nom ?? inv.candidature.entite?.nom}
                                        </strong>
                                        {inv.entrepriseMandataire?.siret && (
                                            <span className="invitation-siret">
                                                {' '}— SIRET {inv.entrepriseMandataire.siret}
                                            </span>
                                        )}
                                    </p>
                                )}
                                {inv.invited_at && (
                                    <p className="invitation-date">
                                        Reçue le{' '}
                                        {new Date(inv.invited_at).toLocaleDateString('fr-FR')}
                                    </p>
                                )}
                            </div>
                            <div className="invitation-actions">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    value="Refuser"
                                    size="small"
                                    onClick={() => handleRefuse(inv.id)}
                                />
                                <Button
                                    type="button"
                                    variant="primary"
                                    value="Accepter"
                                    size="small"
                                    onClick={() => handleAccept(inv.id)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── MesCandidaturesTab ────────────────────────────────────────────────────────

export default function MesCandidaturesTab({
    candidatures,
    invitations,
    loading,
    onRefresh,
    onDeleteCandidature,
    accordCadreName,
    onDemoClick,
    demoLoading,
}: MesCandidaturesTabProps) {
    const navigate = useNavigate();
    const [showGroupementModal, setShowGroupementModal] = useState(false);
    const [signatureCandidatureId, setSignatureCandidatureId] = useState<number | null>(null);
    const [contratPreviewId, setContratPreviewId] = useState<number | null>(null);
    const [contratPreviewBlobUrl, setContratPreviewBlobUrl] = useState<string | null>(null);
    const [contratPreviewLoading, setContratPreviewLoading] = useState(false);

    // ── Annulation de candidature (irréversible) ──────────────────────────────
    // Le bouton n'apparaît que si le back a répondu peut_annuler ; c'est lui qui
    // tranche la fenêtre d'annulation, pas cette liste.
    const [annulerTarget, setAnnulerTarget] = useState<CandidatureResumee | null>(null);
    const [isAnnulling, setIsAnnulling] = useState(false);
    const [annulerError, setAnnulerError] = useState<string | null>(null);

    const handleConfirmAnnuler = async () => {
        if (!annulerTarget) return;
        setIsAnnulling(true);
        setAnnulerError(null);
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${annulerTarget.id}/annuler`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.errors?.candidature?.[0] ?? err?.message ?? `Erreur ${res.status}`);
            }
            setAnnulerTarget(null);
            onRefresh();
        } catch (e) {
            setAnnulerError(e instanceof Error ? e.message : 'Erreur inconnue');
        } finally {
            setIsAnnulling(false);
        }
    };

    const handleVoirContrat = async (id: number) => {
        if (contratPreviewLoading) return;
        setContratPreviewLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${apiUrl}/publications/ac27/contrats/${id}/telecharger`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const blob = await res.blob();
            setContratPreviewId(id);
            setContratPreviewBlobUrl(URL.createObjectURL(blob));
        } catch {
            // silently fail
        } finally {
            setContratPreviewLoading(false);
        }
    };

    const handleDownloadContrat = async () => {
        if (!contratPreviewId) return;
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${apiUrl}/publications/ac27/contrats/${contratPreviewId}/telecharger`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `contrat-signe-${contratPreviewId}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            // silently fail
        }
    };

    const handleCloseContrat = () => {
        if (contratPreviewBlobUrl) URL.revokeObjectURL(contratPreviewBlobUrl);
        setContratPreviewBlobUrl(null);
        setContratPreviewId(null);
    };

    const invitationsPending = invitations.filter((i) => i.status === 'pending');

    // Ouvre la popup automatiquement si des invitations sont en attente
    useEffect(() => {
        if (invitationsPending.length > 0) {
            setShowGroupementModal(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invitationsPending.length]);

    const handleInvitationHandled = () => {
        onRefresh();
    };


    const columns = useMemo(() => [
        {
            field: 'identifiant_candidature',
            header: 'Candidature N°',
            renderCell: (_: any, row: CandidatureResumee) => (
                <div>#{row.identifiant_candidature ?? row.id}</div>
            ),
        },
        {
            field: 'ref_avis',
            header: 'Ref avis',
            renderCell: (value: string) => <div>{value || '—'}</div>,
        },
        {
            field: 'accord_cadre_nom',
            header: 'Accord-Cadre',
            renderCell: (value: string) => <div className="text-truncate" title={value}>{value}</div>,
        },
        {
            field: 'lot_technique_libelle',
            header: 'Lot technique',
            renderCell: (value: string) => <div className="text-truncate" title={value}>{value || '—'}</div>,
        },
        {
            field: 'statut',
            header: 'Statut',
            renderCell: (value: CandidatureResumee['statut'], row: CandidatureResumee) => (
                <StatutBadge statut={value} isDemo={row.source_module === 'demo'} deposee_at={row.deposee_at} annulee_at={row.annulee_at} />
            ),
        },
        {
            field: 'phase',
            header: 'Phase',
            // La phase est affichée dès qu'elle est connue, y compris en phase 1.
            // Les modules sans notion de phase laissent le tiret.
            renderCell: (_: any, row: CandidatureResumee) => (
                <div>{row.phase ?? '—'}</div>
            ),
        },
        {
            field: 'created_at',
            header: 'Créé le',
            renderCell: (value: string) => {
                const d = value ? new Date(value) : null;
                return <div>{d && !isNaN(d.getTime()) ? d.toLocaleDateString('fr-FR') : '—'}</div>;
            },
        },
        {
            field: 'date_limite_candidature',
            header: 'Limite de dépôt',
            renderCell: (value: string) => {
                const d = value ? new Date(value) : null;
                return (
                    <div>
                        {d && !isNaN(d.getTime()) ? d.toLocaleString('fr-FR') : '—'}
                        {d && !isNaN(d.getTime()) && (
                            <span style={{ display: 'block', fontSize: '0.75em', color: '#6b7280' }}>{getParisLabel()}</span>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'date_limite_candidature',
            header: 'Temps restant',
            renderCell: (value: string, row: CandidatureResumee) => {
                if (row.statut !== 'brouillon' && row.statut !== 'a_signer') {
                    return <div>—</div>;
                }
                return <CompteARebourd targetDate={value} autoReload={false} />;
            },
        },
        {
            field: 'id',
            header: 'Actions',
            renderCell: (_: number, row: CandidatureResumee) => {
                if (row.source_module === 'demo') {
                    return (
                        <Button
                            type="button"
                            variant="primary"
                            value={demoLoading ? 'Connexion...' : 'Tester'}
                            size="small"
                            disabled={demoLoading}
                            onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); onDemoClick?.(); }}
                        />
                    );
                }
                return (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {row.statut === 'a_signer' && row.est_mandataire !== false ? (
                            <Button
                                type="button"
                                variant="primary"
                                value="J'accuse réception"
                                size="small"
                                onClick={() => setSignatureCandidatureId(row.id)}
                            />
                        ) : row.statut === 'signee' ? (
                            <Button
                                type="button"
                                variant="secondary"
                                value="Voir le contrat"
                                size="small"
                                disabled={contratPreviewLoading}
                                onClick={() => handleVoirContrat(row.id)}
                            />
                        ) : (
                            <Button
                                type="button"
                                variant={row.statut === 'brouillon' ? 'primary' : 'secondary'}
                                value={row.statut === 'brouillon' ? 'Continuer' : 'Voir'}
                                size="small"
                                onClick={() =>
                                    navigate(`/publications/${row.source_module}/candidatures/${row.id}`)
                                }
                            />
                        )}
                        {row.statut === 'brouillon' && row.est_mandataire !== false && onDeleteCandidature && (
                            <Button
                                type="button"
                                variant="cancel"
                                value=""
                                size="small"
                                className='delete-member-button'
                                icon={<DeleteIcon />}
                                onClick={() => onDeleteCandidature(row.id)}
                            />
                        )}
                        {row.peut_annuler && row.est_mandataire !== false && (
                            <Button
                                type="button"
                                variant="cancel"
                                value="Annuler"
                                size="small"
                                onClick={(e?: React.MouseEvent) => {
                                    e?.stopPropagation();
                                    setAnnulerError(null);
                                    setAnnulerTarget(row);
                                }}
                            />
                        )}
                    </div>
                );
            },
        },
    ], [navigate, onDeleteCandidature, contratPreviewLoading, onDemoClick, demoLoading]);

    return (
        <div className="mes-candidatures-container">
            {/* Badge invitations (si popup fermée et pending restants) */}
            {invitationsPending.length > 0 && !showGroupementModal && (
                <button
                    className="invitations-badge"
                    onClick={() => setShowGroupementModal(true)}
                >
                    <span className="font-semibold">
                        {invitationsPending.length} invitation(s) groupement en attente
                    </span>
                    <span>→ Voir</span>
                </button>
            )}

            {/* Tableau candidatures via composant partagé */}
            {loading ? (
                <div className="mes-candidatures-loading">
                    <div className="spinner"></div>
                    <p>Chargement en cours…</p>
                </div>
            ) : candidatures.length === 0 ? (
                <div className="mes-candidatures-empty">
                    <p>Vous n'avez aucune candidature en cours.</p>
                    <p className="mes-candidatures-empty-hint">
                        Créez une candidature depuis le bouton sur la ligne concernée.
                    </p>
                </div>
            ) : (
                <TableauDeBord
                    data={candidatures}
                    columns={columns}
                    showActionsColumn={false}
                    isSelectable={false}
                    showActionBar={false}
                    selectedItems={[]}
                    onSelectionChange={() => { }}
                    disableDefaultSort={true}
                    onRowClick={(id) => {
                        const cand = candidatures.find((c) => String(c.id) === String(id));
                        if (!cand) return;
                        if (cand.source_module === 'demo') { onDemoClick?.(); return; }
                        navigate(`/publications/${cand.source_module}/candidatures/${cand.id}`);
                    }}
                />
            )}

            {/* Popup groupement */}
            {showGroupementModal && invitationsPending.length > 0 && (
                <ModalInvitationsGroupement
                    invitations={invitationsPending}
                    accordCadreName={accordCadreName}
                    onClose={() => setShowGroupementModal(false)}
                    onHandled={handleInvitationHandled}
                />
            )}

            {/* Modal signature contrat */}
            {signatureCandidatureId && (
                <SignatureFlow
                    candidatureId={signatureCandidatureId}
                    onClose={() => setSignatureCandidatureId(null)}
                    onSigned={onRefresh}
                />
            )}

            {/* Modal visualisation contrat signé */}
            {contratPreviewBlobUrl && (
                <div className="modal-overlay" onClick={handleCloseContrat}>
                    <div className="modal-content-document" onClick={(e) => e.stopPropagation()}>
                        <h3>Contrat signé</h3>
                        <iframe
                            src={contratPreviewBlobUrl}
                            title="Contrat signé"
                            width="100%"
                            height="500px"
                        />
                        <div className="modal-actions">
                            <Button
                                onClick={handleDownloadContrat}
                                icon={<DownloadIcon className="dl-button-icon-white" />}
                                value="Télécharger"
                                size="small"
                            />
                            <Button
                                onClick={handleCloseContrat}
                                value="Fermer"
                                size="small"
                                variant="secondary"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation d'annulation — action définitive, on la fait dire à voix haute */}
            <ValidationPopUp
                isOpen={annulerTarget !== null}
                title={annulerTarget ? titreAnnulationCandidature(numeroCandidature(annulerTarget)) : ''}
                text={
                    annulerError
                        ? `Annulation impossible : ${annulerError}`
                        : annulerTarget
                            ? texteAnnulationCandidature(
                                numeroCandidature(annulerTarget),
                                // Le libellé arrive préfixé ("Lot 102 — …") ; la phrase
                                // porte déjà "pour le lot", on retire le doublon.
                                (annulerTarget.lot_technique_libelle ?? '').replace(/^lot\s+/i, ''),
                            )
                            : ''
                }
                onClose={() => { setAnnulerTarget(null); setAnnulerError(null); }}
                onConfirm={handleConfirmAnnuler}
                cancelLabel="Revenir en arrière"
                confirmLabel="Annuler définitivement"
                confirmVariant="danger"
                isLoading={isAnnulling}
            />
        </div>
    );
}

